import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import BlockOutlined from "@mui/icons-material/BlockOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import ExpandMoreOutlined from "@mui/icons-material/ExpandMoreOutlined";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import AccessTimeOutlined from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import HourglassBottomOutlined from "@mui/icons-material/HourglassBottomOutlined";
import FiberManualRecord from "@mui/icons-material/FiberManualRecord";
import SimplePagination from "../../components/SimplePagination";
import Spinner from "common/components/base/spinner/Spinner";
import { DEFAULT_PAGE_SIZE } from "common/config";
import useOnClickOutside from "common/hooks/useOnClickOutside";
import {
  ONTOLOGY_TABLE_COLUMNS,
  ONTOLOGY_KPI_META,
  OntologyKpi,
  OntologyInitiativeRow,
  OntologyStatus,
  PERFORMANCE_METRICS
} from "../../data/ontology.data";
import {
  OntologyService,
  filterInitiatives,
  OntologyInitiativeQueryParams
} from "../../data/ontology.service";
import "./CreateOntology.scss";

type OrgView = "Business Unit" | "Team" | "Individual";

const ORG_VIEWS: OrgView[] = ["Business Unit", "Team", "Individual"];

const STATUS_OPTIONS: OntologyStatus[] = [
  "Draft",
  "In Review",
  "Approved",
  "Published"
];

const KPI_ICONS: Record<string, React.ReactNode> = {
  active: <AccountTreeOutlined fontSize="small" />,
  draft: <DescriptionOutlined fontSize="small" />,
  "awaiting-review": <AccessTimeOutlined fontSize="small" />,
  published: <CheckCircleOutlined fontSize="small" />
};

const STATUS_CLASS: Record<OntologyStatus, string> = {
  "In Review": "ontology-status-pill--in-review",
  Published: "ontology-status-pill--published",
  Draft: "ontology-status-pill--draft",
  Approved: "ontology-status-pill--approved"
};

// Maps the backend's `currentStageName` to the wizard step that owns editing for that stage.
// Stages without a built page yet fall back to the Setup page, which is the only one wired to PUT.
const STAGE_ROUTES: Record<string, string> = {
  "Capture & Classify": "/ontology-studio/create",
  BQ_GENERATED: "/ontology-studio/create/questions",
  "CQ generation in progress": "/ontology-studio/create/questions",
  "CQ Generation Failed": "/ontology-studio/create/questions",
  CQ_GENERATED: "/ontology-studio/create/questions",
  "Entities & Relationships": "/ontology-studio/create/entities-relationships",
  "Entity Generation in progress":
    "/ontology-studio/create/entities-relationships",
  "Entity Generation Failed": "/ontology-studio/create/questions",
  "Entities Generated": "/ontology-studio/create/entities-relationships",
  "Reuse/Harmonization Completed":
    "/ontology-studio/create/reuse-harmonisation",
  "Reuse/Harmonization in progress":
    "/ontology-studio/create/reuse-harmonisation",
  "Reuse/Harmonization Failed":
    "/ontology-studio/create/entities-relationships",
  "Ground to Documents": "/ontology-studio/create/ground-to-documents",
  "Build Knowledge Graph": "/ontology-studio/create/build-knowledge-graph",
  "Validate & Release": "/ontology-studio/create/validate-release"
};

const getInitiativeRoute = (currentStage: string): string =>
  STAGE_ROUTES[currentStage] ?? "/ontology-studio";

// Human-readable labels for backend stage names whose raw form reads awkwardly in the UI.
const STAGE_DISPLAY_LABELS: Record<string, string> = {
  BQ_GENERATED: "BQ Generated",
  "CQ generation in progress": "CQ Generation in Progress",
  CQ_GENERATED: "CQ Generated",
  "Entity Generation in progress": "Entity Generation in Progress"
};

const getStageLabel = (stage: string): string =>
  STAGE_DISPLAY_LABELS[stage] ?? stage;

// Generates a consistent avatar background color from a name (mirrors the Processing Hub
// "Uploaded by" design in SortingTable.tsx).
const generateColorFromName = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  const hue = Math.abs(hash) % 360;
  const saturation = 65 + (Math.abs(hash) % 20);
  const lightness = 45 + (Math.abs(hash) % 20);

  const hslToHex = (h: number, s: number, l: number): string => {
    l /= 100;
    const a = (s * Math.min(l, 1 - l)) / 100;
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, "0");
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  };

  return hslToHex(hue, saturation, lightness);
};

// Fixed 8-stage ontology lifecycle, in canonical order (not derived from loaded data).
const ONTOLOGY_STAGES = [
  "Capture & Classify",
  "BQ_GENERATED",
  "CQ generation in progress",
  "CQ_GENERATED",
  "Entity Generation in progress",
  "Entities & Relationships",
  "Reuse & Harmonisation",
  "Ground to Documents",
  "Build Knowledge Graph",
  "Validate & Release"
];

// Debounce delay before firing a backend search request while the user is still typing.
const SEARCH_DEBOUNCE_MS = 3000;
// Minimum query length before a backend search request is allowed to fire (throttles short queries).
const SEARCH_MIN_CHARS = 3;

const CreateOntology: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const [activeView, setActiveView] = useState<OrgView>("Business Unit");
  const [kpis, setKpis] = useState<OntologyKpi[]>([]);
  const [allInitiatives, setAllInitiatives] = useState<OntologyInitiativeRow[]>(
    []
  );
  const [businessUnitFilter, setBusinessUnitFilter] = useState("All");
  const [teamFilter, setTeamFilter] = useState("All");
  const [statusFilters, setStatusFilters] = useState<OntologyStatus[]>([]);
  const [stageFilters, setStageFilters] = useState<string[]>([]);
  // Staged copies of Status/Stage bound to the popup's checkboxes; only committed on "Done".
  const [draftStatusFilters, setDraftStatusFilters] = useState<
    OntologyStatus[]
  >([]);
  const [draftStageFilters, setDraftStageFilters] = useState<string[]>([]);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const filterPopupRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [recentActivity, setRecentActivity] = useState<string[]>([]);
  const [businessUnitNames, setBusinessUnitNames] = useState<string[]>([]);
  const [teamNames, setTeamNames] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredResults, setFilteredResults] = useState<
    OntologyInitiativeRow[] | null
  >(null);
  const [initiativePendingDelete, setInitiativePendingDelete] =
    useState<OntologyInitiativeRow | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = useRef(0);
  const didMountFiltersRef = useRef(false);

  useOnClickOutside(searchRef, () => setShowSuggestions(false));
  useOnClickOutside(filterPopupRef, () => setShowFilterPopup(false));

  // Build filter dropdown options from the backend's keyword lists, prefixed with "All".
  const buildOptions = (values: string[]) => [
    "All",
    ...Array.from(new Set(values)).sort()
  ];
  const businessUnitOptions = useMemo(
    () => buildOptions(businessUnitNames),
    [businessUnitNames]
  );
  const teamOptions = useMemo(() => buildOptions(teamNames), [teamNames]);
  const stageOptions = ONTOLOGY_STAGES;
  const hasExtraFilters = statusFilters.length > 0 || stageFilters.length > 0;
  const hasBackendFilter =
    search.trim().length >= SEARCH_MIN_CHARS ||
    businessUnitFilter !== "All" ||
    teamFilter !== "All" ||
    statusFilters.length === 1 ||
    stageFilters.length === 1;

  // Single call fetches KPI summary, recent activity and the full initiative list together.
  useEffect(() => {
    let isMounted = true;
    OntologyService.getOntologyOverview().then((overview) => {
      if (!isMounted) return;
      setKpis(overview.kpis);
      setRecentActivity(overview.recentActivity);
      setBusinessUnitNames(overview.businessUnitNames);
      setTeamNames(overview.teamNames);
      setAllInitiatives(overview.initiatives);
      setIsLoading(false);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  // Queries the initiatives endpoint with the current search text + Business Unit/Team/Status filters.
  // The backend's statusName param is single-value, so it's only sent when exactly one status is checked.
  // Stale in-flight responses are dropped by comparing against the latest request id (throttling).
  const runFilteredFetch = (searchOverride?: string) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const effectiveSearch =
      searchOverride ??
      (search.trim().length >= SEARCH_MIN_CHARS ? search.trim() : undefined);
    const params: OntologyInitiativeQueryParams = {
      search: effectiveSearch,
      businessUnitName:
        businessUnitFilter !== "All" ? businessUnitFilter : undefined,
      teamName: teamFilter !== "All" ? teamFilter : undefined,
      statusName: statusFilters.length === 1 ? statusFilters[0] : undefined,
      stageName: stageFilters.length === 1 ? stageFilters[0] : undefined
    };

    if (
      !params.search &&
      !params.businessUnitName &&
      !params.teamName &&
      !params.statusName &&
      !params.stageName
    ) {
      searchRequestIdRef.current += 1;
      setFilteredResults(null);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const requestId = ++searchRequestIdRef.current;
    OntologyService.getFilteredInitiatives(params).then((results) => {
      if (requestId !== searchRequestIdRef.current) return;
      setFilteredResults(results);
      setIsSearching(false);
    });
  };

  // Fires the backend request right away, bypassing any pending debounce timer.
  const runSearch = (trimmed: string) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    runFilteredFetch(trimmed || undefined);
  };

  // Debounces the search box, then queries the backend directly instead of filtering client-side.
  useEffect(() => {
    const trimmed = search.trim();
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    if (trimmed.length < SEARCH_MIN_CHARS) {
      runFilteredFetch();
      return;
    }

    setIsSearching(true);
    searchDebounceRef.current = setTimeout(() => {
      runFilteredFetch(trimmed);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search]);

  // Business Unit/Team/single-Status/single-Stage changes are deliberate selections, so query the
  // backend immediately. Resetting back to "All"/none just clears filteredResults instead of
  // re-fetching the unfiltered list, since that list is already held in allInitiatives from the
  // initial overview call.
  useEffect(() => {
    if (!didMountFiltersRef.current) {
      didMountFiltersRef.current = true;
      return;
    }

    const effectiveSearch =
      search.trim().length >= SEARCH_MIN_CHARS ? search.trim() : undefined;
    const params: OntologyInitiativeQueryParams = {
      search: effectiveSearch,
      businessUnitName:
        businessUnitFilter !== "All" ? businessUnitFilter : undefined,
      teamName: teamFilter !== "All" ? teamFilter : undefined,
      statusName: statusFilters.length === 1 ? statusFilters[0] : undefined,
      stageName: stageFilters.length === 1 ? stageFilters[0] : undefined
    };
    const isUnfiltered =
      !params.search &&
      !params.businessUnitName &&
      !params.teamName &&
      !params.statusName &&
      !params.stageName;

    if (isUnfiltered) {
      searchRequestIdRef.current += 1;
      setFilteredResults(null);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const requestId = ++searchRequestIdRef.current;
    OntologyService.getFilteredInitiatives(params).then((results) => {
      if (requestId !== searchRequestIdRef.current) return;
      setFilteredResults(results);
      setIsSearching(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitFilter, teamFilter, statusFilters, stageFilters]);

  // Status/Stage remain client-side filters layered on top of the backend search/Business Unit/Team results.
  const filteredInitiatives = useMemo(
    () =>
      filterInitiatives(
        hasBackendFilter ? filteredResults ?? [] : allInitiatives,
        {
          status: statusFilters,
          currentStage: stageFilters
        }
      ),
    [
      allInitiatives,
      hasBackendFilter,
      filteredResults,
      statusFilters,
      stageFilters
    ]
  );

  const totalInitiatives = filteredInitiatives.length;
  const initiatives = useMemo(
    () =>
      filteredInitiatives.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
      ),
    [filteredInitiatives, currentPage, pageSize]
  );
  const suggestions = filteredInitiatives;

  // Any search or filter change should re-start from page 1 of the results.
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleFilterChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (value: string) => {
      setter(value);
      setCurrentPage(1);
    };

  const toggleStatusFilter = (status: OntologyStatus) => {
    setDraftStatusFilters((prev) =>
      prev.includes(status)
        ? prev.filter((item) => item !== status)
        : [...prev, status]
    );
  };

  const toggleStageFilter = (stage: string) => {
    setDraftStageFilters((prev) =>
      prev.includes(stage)
        ? prev.filter((item) => item !== stage)
        : [...prev, stage]
    );
  };

  // Discards any unsaved checkbox changes and reloads the popup from the currently applied filters.
  const openFilterPopup = () => {
    setDraftStatusFilters(statusFilters);
    setDraftStageFilters(stageFilters);
    setShowFilterPopup(true);
  };

  // Clears the staged (not-yet-applied) selections; still requires "Done" to take effect.
  const clearExtraFilters = () => {
    setDraftStatusFilters([]);
    setDraftStageFilters([]);
  };

  // Commits the staged Status/Stage selections as the actual applied filters.
  const applyExtraFilters = () => {
    setStatusFilters(draftStatusFilters);
    setStageFilters(draftStageFilters);
    setCurrentPage(1);
    setShowFilterPopup(false);
  };

  const handlePaginationChange = (page: number, limit: number) => {
    setCurrentPage(page);
    setPageSize(limit);
  };

  const handleSelectSuggestion = (name: string) => {
    handleSearchChange(name);
    setShowSuggestions(false);
  };

  // Deletes the initiative via the real backend, then removes it from local state.
  const removeInitiative = (id: string) => {
    setAllInitiatives((prev) => prev.filter((row) => row.id !== id));
    setFilteredResults((prev) =>
      prev ? prev.filter((row) => row.id !== id) : prev
    );
    OntologyService.deleteOntologyInitiative(id);
  };

  const askDeleteInitiative = (row: OntologyInitiativeRow) => {
    setInitiativePendingDelete(row);
  };

  const cancelDeleteInitiative = () => {
    setInitiativePendingDelete(null);
  };

  const confirmDeleteInitiative = () => {
    if (!initiativePendingDelete) return;
    removeInitiative(initiativePendingDelete.id);
    setInitiativePendingDelete(null);
  };

  const searchTooShort =
    search.trim().length > 0 && search.trim().length < SEARCH_MIN_CHARS;

  return (
    <div className="ontology-container">
      <nav className="ontology-breadcrumb">
        <Link to="/home" className="breadcrumb-link">
          Engineering Information Management
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">Ontology Studio</span>
      </nav>

      <div className="ontology-page-header">
        <div className="ontology-page-header__title-row">
          <h1 className="ontology-page-title">Ontology Studio</h1>
          <span className="ontology-help-badge" title="Ontology lifecycle help">
            ?
          </span>
        </div>
        <div className="ontology-page-header__bottom-row">
          <p className="ontology-page-description">
            Build ontology initiatives for specific business needs in
            combination with the existing ontology. Monitor lifecycle progress
            and governance across the knowledge modeling lifecycle.
          </p>
          <button
            type="button"
            className="ontology-btn ontology-btn--primary"
            onClick={() => navigate("/ontology-studio/create")}
          >
            <AddIcon fontSize="small" />
            <span>New Initiative</span>
          </button>
        </div>
      </div>

      <section className="ontology-kpis">
        {ONTOLOGY_KPI_META.map((meta) => (
          <div className="ontology-kpi-card" key={meta.id}>
            <div className="ontology-kpi-card__top">
              <span className="ontology-kpi-card__label">{meta.label}</span>
              <span className="ontology-kpi-card__icon">
                {KPI_ICONS[meta.id]}
              </span>
            </div>
            <div className="ontology-kpi-card__value-row">
              {isLoading ? (
                <Spinner size={20} className="ontology-kpi-card__spinner" />
              ) : (
                <span className="ontology-kpi-card__value">
                  {kpis.find((kpi) => kpi.id === meta.id)?.value ?? "-"}
                </span>
              )}
            </div>
            {meta.id === "published" && (
              <button
                type="button"
                className="ontology-kpi-card__library-link"
                onClick={() => navigate("/ontology-studio/library")}
              >
                View Ontology Library
              </button>
            )}
          </div>
        ))}
      </section>

      <section className="ontology-workspace-card">
        <div className="ontology-performance-card">
          <div className="ontology-performance-card__header">
            <div className="ontology-performance-card__title-group">
              <h3 className="ontology-performance-card__title">
                Performance Metrics
              </h3>
              <p className="ontology-performance-card__subtitle">
                Ontology initiative health and throughput metrics across the
                finalized 8-stage lifecycle.
              </p>
            </div>
            <span className="ontology-performance-card__period">
              This Month
            </span>
          </div>
          <div className="ontology-performance-card__metrics">
            {PERFORMANCE_METRICS.map((metric) => (
              <div className="ontology-metric-tile" key={metric.id}>
                <div className="ontology-metric-tile__label-row">
                  <span
                    className="ontology-metric-tile__dot"
                    style={{ backgroundColor: metric.dotColor }}
                  />
                  <span className="ontology-metric-tile__label">
                    {metric.label}
                  </span>
                </div>
                {isLoading ? (
                  <Spinner
                    size={16}
                    className="ontology-metric-tile__spinner"
                  />
                ) : (
                  <span className="ontology-metric-tile__value">
                    {metric.value}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="ontology-workspace-card__header">
          <div className="ontology-workspace-card__header-top">
            <div className="ontology-workspace-card__heading">
              <h3 className="ontology-workspace-card__title">
                Ontology Initiatives
              </h3>
              <p className="ontology-workspace-card__subtitle">
                Track ontology creation, competency questions, validation
                progress and governance status.
              </p>
            </div>
            <div className="ontology-workspace-card__tools">
              <div
                className={`ontology-search${
                  searchTooShort ? " ontology-search--warning" : ""
                }`}
                ref={searchRef}
              >
                <SearchIcon
                  className="ontology-search__icon"
                  fontSize="small"
                />
                <input
                  type="text"
                  className="ontology-search__input"
                  placeholder="Search initiatives..."
                  value={search}
                  onChange={(event) => {
                    handleSearchChange(event.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") runSearch(search.trim());
                  }}
                />
                {searchTooShort && (
                  <span className="ontology-search__hint">
                    {SEARCH_MIN_CHARS} characters required for search
                  </span>
                )}
                {/* {showSuggestions && search.trim() && (
                  <ul className="ontology-search__suggestions">
                    {suggestions.length === 0 ? (
                      <li className="ontology-search__suggestion ontology-search__suggestion--empty">
                        No matching initiatives
                      </li>
                    ) : (
                      suggestions.map((row) => (
                        <li key={row.id}>
                          <button
                            type="button"
                            className="ontology-search__suggestion"
                            onClick={() => handleSelectSuggestion(row.name)}
                          >
                            {row.name}
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )} */}
              </div>
              <div
                className="ontology-filter-popup-anchor"
                ref={filterPopupRef}
              >
                <button
                  type="button"
                  className={`ontology-btn ontology-btn--secondary${
                    hasExtraFilters ? " ontology-btn--active" : ""
                  }`}
                  onClick={() =>
                    showFilterPopup
                      ? setShowFilterPopup(false)
                      : openFilterPopup()
                  }
                >
                  <TuneOutlined fontSize="small" />
                  <span>Filters</span>
                  {hasExtraFilters && (
                    <span className="ontology-filter-count">
                      {statusFilters.length + stageFilters.length}
                    </span>
                  )}
                </button>
                {showFilterPopup && (
                  <div className="ontology-filter-popup">
                    <div className="ontology-filter-popup__section">
                      <h4 className="ontology-filter-popup__title">Status</h4>
                      {STATUS_OPTIONS.map((status) => (
                        <label
                          key={status}
                          className="ontology-filter-popup__checkbox"
                        >
                          <input
                            type="checkbox"
                            checked={draftStatusFilters.includes(status)}
                            onChange={() => toggleStatusFilter(status)}
                          />
                          {status}
                        </label>
                      ))}
                    </div>
                    <div className="ontology-filter-popup__section">
                      <h4 className="ontology-filter-popup__title">Stage</h4>
                      {stageOptions.map((stage) => (
                        <label
                          key={stage}
                          className="ontology-filter-popup__checkbox"
                        >
                          <input
                            type="checkbox"
                            checked={draftStageFilters.includes(stage)}
                            onChange={() => toggleStageFilter(stage)}
                          />
                          {getStageLabel(stage)}
                        </label>
                      ))}
                    </div>
                    <div className="ontology-filter-popup__footer">
                      <button
                        type="button"
                        className="ontology-filter-popup__clear"
                        onClick={clearExtraFilters}
                        disabled={
                          draftStatusFilters.length === 0 &&
                          draftStageFilters.length === 0
                        }
                      >
                        Clear all
                      </button>
                      <button
                        type="button"
                        className="ontology-filter-popup__done"
                        onClick={applyExtraFilters}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="ontology-workspace-card__header-bottom">
            <div className="ontology-table-filters">
              <label className="ontology-table-filter">
                <span className="ontology-table-filter__label">
                  Business Unit
                </span>
                <div className="ontology-table-filter__control">
                  <select
                    value={businessUnitFilter}
                    onChange={(event) =>
                      handleFilterChange(setBusinessUnitFilter)(
                        event.target.value
                      )
                    }
                  >
                    {businessUnitOptions.map((option) => (
                      <option key={option} value={option}>
                        {option === "All" ? "All Business Units" : option}
                      </option>
                    ))}
                  </select>
                  <ExpandMoreOutlined
                    className="ontology-table-filter__chevron"
                    fontSize="small"
                  />
                </div>
              </label>
              <label className="ontology-table-filter ontology-table-filter--team">
                <span className="ontology-table-filter__label">
                  Team / Project Group
                </span>
                <div className="ontology-table-filter__control">
                  <select
                    value={teamFilter}
                    onChange={(event) =>
                      handleFilterChange(setTeamFilter)(event.target.value)
                    }
                  >
                    {teamOptions.map((option) => (
                      <option key={option} value={option}>
                        {option === "All" ? "All Teams" : option}
                      </option>
                    ))}
                  </select>
                  <ExpandMoreOutlined
                    className="ontology-table-filter__chevron"
                    fontSize="small"
                  />
                </div>
              </label>
            </div>
            <span className="ontology-pill ontology-pill--reviews">
              <span className="ontology-pill__dot" />3 Pending Reviews
            </span>
          </div>
        </div>

        <div className="ontology-table-scroll">
          <table className="ontology-table">
            <thead>
              <tr>
                {ONTOLOGY_TABLE_COLUMNS.map((column) => (
                  <th key={column}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(isLoading || isSearching) && (
                <tr>
                  <td
                    colSpan={ONTOLOGY_TABLE_COLUMNS.length}
                    className="ontology-table__muted"
                  >
                    <div className="ontology-table__loading">
                      <Spinner size={15} />
                    </div>
                  </td>
                </tr>
              )}
              {!isLoading && !isSearching && initiatives.length === 0 && (
                <tr>
                  <td
                    colSpan={ONTOLOGY_TABLE_COLUMNS.length}
                    className="ontology-table__muted"
                  >
                    No ontology initiatives found.
                  </td>
                </tr>
              )}
              {!isLoading &&
                !isSearching &&
                initiatives.map((row) => (
                  <tr key={row.id}>
                    <td className="ontology-table__name">
                      <button
                        type="button"
                        className="ontology-table__name-link"
                        onClick={() =>
                          navigate(getInitiativeRoute(row.currentStage), {
                            state: { initiativeId: row.id }
                          })
                        }
                      >
                        {row.name}
                      </button>
                    </td>
                    <td>
                      <div className="ontology-owner">
                        <div
                          className="ontology-owner__avatar"
                          style={{
                            backgroundColor: generateColorFromName(row.owner)
                          }}
                        >
                          {row.owner.charAt(0).toUpperCase()}
                        </div>
                        <span>{row.owner}</span>
                      </div>
                    </td>
                    <td>
                      <div className="ontology-stage-status">
                        {row.currentStage.toLowerCase().includes("progress") ? (
                          <HourglassBottomOutlined
                            className="ontology-stage-status__icon"
                            style={{ color: "#f59e0b" }}
                          />
                        ) : row.currentStage.toLowerCase().includes("failed") ||
                          row.currentStage.toLowerCase().includes("error") ? (
                          <FiberManualRecord
                            className="ontology-stage-status__icon"
                            style={{ color: "red" }}
                          />
                        ) : (
                          <CheckCircleOutlined className="ontology-stage-status__icon" />
                        )}
                        <span>{getStageLabel(row.currentStage)}</span>
                      </div>
                    </td>
                    <td>{row.businessUnitName}</td>
                    <td>{row.teamName}</td>
                    <td>{row.lastUpdated}</td>
                    <td>
                      <span
                        className={`ontology-status-pill ${
                          STATUS_CLASS[row.status]
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td>
                      <div className="ontology-action-group">
                        <button
                          type="button"
                          className="ontology-action-btn"
                          aria-label="View initiative"
                          disabled={row.status !== "Published"}
                          onClick={() =>
                            navigate(getInitiativeRoute(row.currentStage), {
                              state: { initiativeId: row.id }
                            })
                          }
                        >
                          <span className="ontology-action-btn__icon-stack">
                            <VisibilityOutlined
                              fontSize="small"
                              className="ontology-action-btn__icon ontology-action-btn__icon--primary"
                            />
                            <BlockOutlined
                              fontSize="small"
                              className="ontology-action-btn__icon ontology-action-btn__icon--restrict"
                            />
                          </span>
                        </button>
                        <button
                          type="button"
                          className="ontology-action-btn"
                          aria-label="Edit initiative"
                          disabled={row.status === "Published"}
                          onClick={() =>
                            navigate(getInitiativeRoute(row.currentStage), {
                              state: { initiativeId: row.id }
                            })
                          }
                        >
                          <span className="ontology-action-btn__icon-stack">
                            <EditOutlined
                              fontSize="small"
                              className="ontology-action-btn__icon ontology-action-btn__icon--primary"
                            />
                            <BlockOutlined
                              fontSize="small"
                              className="ontology-action-btn__icon ontology-action-btn__icon--restrict"
                            />
                          </span>
                        </button>
                        <button
                          type="button"
                          className="ontology-action-btn"
                          aria-label="Delete initiative"
                          disabled={row.status === "Published"}
                          onClick={() => askDeleteInitiative(row)}
                        >
                          <span className="ontology-action-btn__icon-stack">
                            <DeleteOutline
                              fontSize="small"
                              className="ontology-action-btn__icon ontology-action-btn__icon--primary"
                            />
                            <BlockOutlined
                              fontSize="small"
                              className="ontology-action-btn__icon ontology-action-btn__icon--restrict"
                            />
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className="ontology-table-footer">
          <span className="ontology-table-footer__info">
            Showing{" "}
            {initiatives.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}-
            {(currentPage - 1) * pageSize + initiatives.length} of{" "}
            {totalInitiatives} registered ontology initiatives
          </span>
          {totalInitiatives > 5 && (
            <SimplePagination
              currentPage={currentPage}
              totalPages={Math.max(1, Math.ceil(totalInitiatives / pageSize))}
              onPageChange={(page) => handlePaginationChange(page, pageSize)}
            />
          )}
        </div>

        <div className="ontology-recent-activity">
          <h4 className="ontology-recent-activity__title">Recent Activity</h4>
          <ul className="ontology-recent-activity__list">
            {isLoading ? (
              <li className="ontology-recent-activity__item ontology-recent-activity__item--loading">
                <Spinner size={16} />
              </li>
            ) : recentActivity.length === 0 ? (
              <li className="ontology-recent-activity__item ontology-recent-activity__item--empty">
                No recent activity.
              </li>
            ) : (
              recentActivity.map((activity) => (
                <li key={activity} className="ontology-recent-activity__item">
                  <span className="ontology-recent-activity__dot" />
                  {activity}
                </li>
              ))
            )}
          </ul>
        </div>
      </section>

      {initiativePendingDelete && (
        <div className="ontology-confirm-root">
          <div
            className="ontology-confirm-overlay"
            onClick={cancelDeleteInitiative}
          />
          <div className="ontology-confirm-modal">
            <div className="ontology-confirm-modal__content">
              <div className="ontology-confirm-modal__heading">
                <p className="ontology-confirm-modal__title">
                  Delete Ontology Initiative?
                </p>
                <p className="ontology-confirm-modal__subtitle">
                  You are about to delete this Ontology Initiative.
                </p>
              </div>
              <div className="ontology-confirm-modal__record">
                <p className="ontology-confirm-modal__record-label">
                  Selected initiative
                </p>
                <p className="ontology-confirm-modal__record-text">
                  {initiativePendingDelete.name}
                </p>
              </div>
              <div className="ontology-confirm-modal__warning">
                <WarningAmberOutlined fontSize="small" />
                <p>
                  Deleting this initiative will remove all associated ontology
                  artifacts and traceability links.
                </p>
              </div>
            </div>
            <div className="ontology-confirm-modal__actions">
              <button
                type="button"
                className="ontology-confirm-modal__cancel"
                onClick={cancelDeleteInitiative}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ontology-confirm-modal__delete"
                onClick={confirmDeleteInitiative}
              >
                Delete Initiative
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateOntology;
